import { create } from 'zustand';

/**
 * First-login onboarding (spec §14): language → appearance → a 30-second controls tutorial.
 * Whether someone has seen the tutorial is remembered per account in this browser.
 */

const tutorialKey = (userId: string) => `veriverse.tutorial.${userId}`;

export function tutorialSeen(userId: string): boolean {
  try {
    return localStorage.getItem(tutorialKey(userId)) === 'done';
  } catch {
    return true; // without storage, do not nag on every visit
  }
}

export function markTutorialSeen(userId: string): void {
  try {
    localStorage.setItem(tutorialKey(userId), 'done');
  } catch {
    /* non-essential */
  }
}

export const TUTORIAL_STEPS = ['move', 'interact', 'chat', 'settings'] as const;
export type TutorialStep = (typeof TUTORIAL_STEPS)[number];

/** The step after `step`, or `undefined` when the tutorial is finished. */
export function nextStep(step: TutorialStep): TutorialStep | undefined {
  return TUTORIAL_STEPS[TUTORIAL_STEPS.indexOf(step) + 1];
}

interface OnboardingState {
  /** The tutorial step on screen, or `undefined` when it is closed. */
  tutorial: TutorialStep | undefined;
  startTutorial(): void;
  advance(from: TutorialStep): void;
  closeTutorial(): void;
}

export const useOnboardingStore = create<OnboardingState>()((set) => ({
  tutorial: undefined,
  startTutorial: () => set({ tutorial: TUTORIAL_STEPS[0] }),
  // Only advances from the step on screen, so a stray second key press cannot skip one.
  advance: (from) =>
    set((state) => (state.tutorial === from ? { tutorial: nextStep(from) } : state)),
  closeTutorial: () => set({ tutorial: undefined }),
}));
